import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Layout, Card, Table, Button, Space, message, Modal, Form, Input, InputNumber, Popconfirm, Menu, Switch, Avatar, Tag, Tooltip, Upload, Tabs, type MenuProps } from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BookOutlined,
  UserOutlined,
  MessageOutlined,
  SettingOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  LogoutOutlined,
  HomeOutlined,
  UploadOutlined,
  InboxOutlined,
  LinkOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ApiResponse } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { getMediaUrl } from '../../utils/request';
import {
  getKnowledgeQAList,
  createKnowledgeQA,
  updateKnowledgeQA,
  deleteKnowledgeQA,
  getSmsConfig,
  updateSmsConfig,
  getSystemSettings,
  updateSystemSettings,
  getQuickLinks,
  createQuickLink,
  updateQuickLink,
  deleteQuickLink,
  getMemberList,
  toggleMemberStatus,
  deleteMember,
  uploadFile,
  type KnowledgeQAItem,
  type KnowledgeQACreateParams,
  type SmsConfigData,
  type SystemSettingsData,
  type QuickLinkItem,
  type MemberItem,
} from '../../api/admin';
import './index.css';

const { Header, Content, Sider } = Layout;
const { Dragger } = Upload;

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [activeMenu, setActiveMenu] = useState<string>('knowledge');
  
  // Knowledge QA State
  const [list, setList] = useState<KnowledgeQAItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<KnowledgeQAItem | null>(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);

  // SMS Config State
  const [smsConfig, setSmsConfig] = useState<SmsConfigData | null>(null);
  const [smsConfigLoading, setSmsConfigLoading] = useState(false);
  const [smsForm] = Form.useForm();
  const [smsSaveLoading, setSmsSaveLoading] = useState(false);

  // System Settings State
  const [systemSettings, setSystemSettings] = useState<SystemSettingsData | null>(null);
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemForm] = Form.useForm();
  const [systemSaveLoading, setSystemSaveLoading] = useState(false);

  // Quick Link State
  const [quickLinks, setQuickLinks] = useState<QuickLinkItem[]>([]);
  const [quickLinksLoading, setQuickLinksLoading] = useState(false);
  const [quickLinkModalVisible, setQuickLinkModalVisible] = useState(false);
  const [editingQuickLink, setEditingQuickLink] = useState<QuickLinkItem | null>(null);
  const [quickLinkForm] = Form.useForm();
  const [quickLinkSubmitLoading, setQuickLinkSubmitLoading] = useState(false);
  const [quickLinkImageList, setQuickLinkImageList] = useState<any[]>([]);

  // Member Management State
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Upload State
  const [imageList, setImageList] = useState<any[]>([]);
  const [videoList, setVideoList] = useState<any[]>([]);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await getKnowledgeQAList();
      const data = (res as ApiResponse<KnowledgeQAItem[]>)?.data;
      setList(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadSmsConfig = async () => {
    setSmsConfigLoading(true);
    try {
      const res = await getSmsConfig();
      const data = (res as ApiResponse<SmsConfigData>)?.data;
      if (data) {
        setSmsConfig(data);
        smsForm.setFieldsValue({
          use_aliyun: data.use_aliyun,
          access_key_id: data.access_key_id || '',
          access_key_secret: data.access_key_secret || '',
          sign_name: data.sign_name || '',
          template_code: data.template_code || '',
        });
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '加载短信配置失败');
    } finally {
      setSmsConfigLoading(false);
    }
  };

  const loadSystemSettings = async () => {
    setSystemLoading(true);
    try {
      const res = await getSystemSettings();
      const data = (res as ApiResponse<SystemSettingsData>)?.data;
      if (data) {
        setSystemSettings(data);
        systemForm.setFieldsValue(data);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '加载系统设置失败');
    } finally {
      setSystemLoading(false);
    }
  };

  const loadQuickLinks = async () => {
    setQuickLinksLoading(true);
    try {
      const res = await getQuickLinks();
      const data = (res as ApiResponse<QuickLinkItem[]>)?.data;
      setQuickLinks(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '加载快捷链接失败');
    } finally {
      setQuickLinksLoading(false);
    }
  };

  const onSystemSettingsFinish = async (values: Partial<SystemSettingsData>) => {
    setSystemSaveLoading(true);
    try {
      await updateSystemSettings(values);
      message.success('保存成功');
      loadSystemSettings();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '保存失败');
    } finally {
      setSystemSaveLoading(false);
    }
  };

  const onQuickLinkFinish = async () => {
    try {
      const values = await quickLinkForm.validateFields();
      setQuickLinkSubmitLoading(true);

      const imageUrl = quickLinkImageList[0]?.url || quickLinkImageList[0]?.response?.data?.url || '';
      const relativeImageUrl = imageUrl ? (imageUrl.split('/static/uploads/').length > 1 ? `/static/uploads/${imageUrl.split('/static/uploads/')[1]}` : imageUrl) : '';

      const data = {
        ...values,
        image_url: relativeImageUrl,
        icon_svg: values.icon_svg,
        is_visible: values.is_visible ? 1 : 0,
      };

      if (editingQuickLink) {
        await updateQuickLink(editingQuickLink.id, data);
        message.success('更新成功');
      } else {
        await createQuickLink(data);
        message.success('创建成功');
      }
      setQuickLinkModalVisible(false);
      loadQuickLinks();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || '操作失败');
    } finally {
      setQuickLinkSubmitLoading(false);
    }
  };

  const handleDeleteQuickLink = async (id: number) => {
    try {
      await deleteQuickLink(id);
      message.success('删除成功');
      loadQuickLinks();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '删除失败');
    }
  };

  const loadMembers = async () => {
    setMembersLoading(true);
    try {
      const res = await getMemberList();
      const data = (res as ApiResponse<MemberItem[]>)?.data;
      setMembers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '加载会员失败');
    } finally {
      setMembersLoading(false);
    }
  };

  const onSmsConfigFinish = async (values: SmsConfigData) => {
    setSmsSaveLoading(true);
    try {
      await updateSmsConfig(values);
      message.success('保存成功');
      loadSmsConfig();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '保存失败');
    } finally {
      setSmsSaveLoading(false);
    }
  };

  const handleToggleMemberStatus = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await toggleMemberStatus(id, newStatus);
      message.success('操作成功');
      loadMembers();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '操作失败');
    }
  };

  const handleDeleteMember = async (id: number) => {
    try {
      await deleteMember(id);
      message.success('删除成功');
      loadMembers();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '删除失败');
    }
  };

  useEffect(() => {
    if (activeMenu === 'knowledge') loadList();
    if (activeMenu === 'system') {
      loadSystemSettings();
      loadSmsConfig();
    }
    if (activeMenu === 'quicklinks') loadQuickLinks();
    if (activeMenu === 'members') loadMembers();
  }, [activeMenu]);

  const openAdd = () => {
    setEditingItem(null);
    setModalVisible(true);
  };

  const openEdit = (record: KnowledgeQAItem) => {
    setEditingItem(record);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  const openQuickLinkAdd = () => {
    setEditingQuickLink(null);
    setQuickLinkModalVisible(true);
    quickLinkForm.resetFields();
    quickLinkForm.setFieldsValue({ is_visible: true, sort_order: 0 });
    setQuickLinkImageList([]);
  };

  const openQuickLinkEdit = (record: QuickLinkItem) => {
    setEditingQuickLink(record);
    setQuickLinkModalVisible(true);
    quickLinkForm.setFieldsValue({
      ...record,
      is_visible: record.is_visible === 1,
    });
    if (record.image_url) {
      setQuickLinkImageList([
        {
          uid: '-1',
          name: record.image_url.split('/').pop(),
          status: 'done',
          url: getMediaUrl(record.image_url),
        },
      ]);
    } else {
      setQuickLinkImageList([]);
    }
  };

  useLayoutEffect(() => {
    if (!modalVisible) return;
    if (editingItem) {
      form.setFieldsValue({
        question: editingItem.question ?? '',
        answer: editingItem.answer ?? '',
        images: editingItem.images ?? '',
        videos: editingItem.videos ?? '',
        sort_order: editingItem.sort_order ?? 0,
      });
      
      // 设置图片上传列表
      if (editingItem.images) {
        const images = editingItem.images.split(/[\n,]/).filter(url => url.trim()).map((url, index) => ({
          uid: `-img-${index}`,
          name: url.split('/').pop(),
          status: 'done',
          url: getMediaUrl(url.trim()),
          thumbUrl: getMediaUrl(url.trim()),
        }));
        setImageList(images);
      } else {
        setImageList([]);
      }

      // 设置视频上传列表
      if (editingItem.videos) {
        const videos = editingItem.videos.split(/[\n,]/).filter(url => url.trim()).map((url, index) => ({
          uid: `-vid-${index}`,
          name: url.split('/').pop(),
          status: 'done',
          url: getMediaUrl(url.trim()),
        }));
        setVideoList(videos);
      } else {
        setVideoList([]);
      }
    } else {
      form.setFieldsValue({
        question: '',
        answer: '',
        images: '',
        videos: '',
        sort_order: 0,
      });
      setImageList([]);
      setVideoList([]);
    }
  }, [modalVisible, editingItem, form]);

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // 提取相对路径进行保存，避免硬编码域名
      const getRelativePath = (url: string) => {
        if (!url) return '';
        const parts = url.split('/static/uploads/');
        return parts.length > 1 ? `/static/uploads/${parts[1]}` : url;
      };

      // 将上传列表转换为 URL 字符串
      const imageUrls = imageList
        .filter(file => file.status === 'done')
        .map(file => getRelativePath(file.url || file.response?.data?.url))
        .filter(Boolean)
        .join('\n');
      
      const videoUrls = videoList
        .filter(file => file.status === 'done')
        .map(file => getRelativePath(file.url || file.response?.data?.url))
        .filter(Boolean)
        .join('\n');

      setSubmitLoading(true);
      if (editingItem) {
        await updateKnowledgeQA(editingItem.id, {
          question: values.question,
          answer: values.answer,
          images: imageUrls,
          videos: videoUrls,
          sort_order: values.sort_order,
        });
        message.success('更新成功');
      } else {
        await createKnowledgeQA({
          question: values.question,
          answer: values.answer,
          images: imageUrls,
          videos: videoUrls,
          sort_order: values.sort_order,
        } as KnowledgeQACreateParams);
        message.success('添加成功');
      }
      setModalVisible(false);
      setEditingItem(null);
      form.resetFields();
      setImageList([]);
      setVideoList([]);
      loadList();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || '操作失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  const customUpload = async ({ file, onSuccess, onError, onProgress }: any) => {
    try {
      const res = await uploadFile(file);
      if (res.code === 200) {
        // 在成功回调中补充完整 URL，方便即时预览
        const fullUrl = getMediaUrl(res.data.url);
        onSuccess({ ...res, data: { ...res.data, url: fullUrl } });
      } else {
        onError(new Error(res.message));
      }
    } catch (e: any) {
      onError(e);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteKnowledgeQA(id);
      message.success('删除成功');
      loadList();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '删除失败');
    }
  };

  const knowledgeColumns = [
    {
      title: '序号',
      width: 60,
      render: (_: any, __: KnowledgeQAItem, index: number) => index + 1,
    },
    {
      title: '问题',
      dataIndex: 'question',
      key: 'question',
      ellipsis: true,
    },
    {
      title: '答案',
      dataIndex: 'answer',
      key: 'answer',
      ellipsis: true,
      render: (text: string) => (text?.length > 60 ? text.slice(0, 60) + '...' : text),
    },
    {
      title: '多媒体',
      key: 'media',
      width: 100,
      render: (_: any, record: KnowledgeQAItem) => (
        <Space>
          {record.images && (
            <Tooltip title="包含图片">
              <PictureOutlined style={{ color: '#1890ff' }} />
            </Tooltip>
          )}
          {record.videos && (
            <Tooltip title="包含视频">
              <VideoCameraOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          )}
          {!record.images && !record.videos && <span style={{ color: '#ccc' }}>-</span>}
        </Space>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_: any, record: KnowledgeQAItem) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除这条问答吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const quickLinkColumns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '简介',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '图标/图片',
      key: 'media',
      render: (_: any, record: QuickLinkItem) => (
        <Space direction="vertical" size="small">
          {record.icon_svg ? (
             <div 
               className="admin-quicklink-icon-preview"
               style={{ width: 32, height: 32, border: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
               dangerouslySetInnerHTML={{ __html: record.icon_svg }}
             />
          ) : (
            <span style={{ fontSize: 12, color: '#ccc' }}>无SVG图标</span>
          )}
          {record.image_url ? (
            <Avatar shape="square" size={64} src={getMediaUrl(record.image_url)} />
          ) : (
            <span style={{ fontSize: 12, color: '#ccc' }}>无PC图片</span>
          )}
        </Space>
      ),
    },
    {
      title: '链接',
      dataIndex: 'link_url',
      key: 'link_url',
      ellipsis: true,
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
    },
    {
      title: '显示',
      dataIndex: 'is_visible',
      key: 'is_visible',
      render: (visible: number, record: QuickLinkItem) => (
        <Switch 
          checked={visible === 1} 
          onChange={(checked) => {
            updateQuickLink(record.id, { is_visible: checked ? 1 : 0 })
              .then(() => {
                message.success('更新成功');
                loadQuickLinks();
              })
              .catch(() => message.error('更新失败'));
          }} 
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_: any, record: QuickLinkItem) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openQuickLinkEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个快捷链接吗？"
            onConfirm={() => handleDeleteQuickLink(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const memberColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '用户信息',
      key: 'userInfo',
      render: (_: any, record: MemberItem) => (
        <Space>
          <Avatar src={getMediaUrl(record.avatar)} icon={<UserOutlined />} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 'bold' }}>{record.username}</span>
            <span style={{ fontSize: '12px', color: '#888' }}>{record.email}</span>
          </div>
        </Space>
      ),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string | null) => phone || <span style={{ color: '#ccc' }}>未绑定</span>,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'admin' ? 'gold' : 'blue'}>
          {role === 'admin' ? '管理员' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number, record: MemberItem) => (
        <Switch
          checked={status === 1}
          onChange={() => handleToggleMemberStatus(record.id, status)}
          checkedChildren="正常"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: MemberItem) => (
        <Popconfirm
          title="确定删除该会员吗？此操作不可逆！"
          onConfirm={() => handleDeleteMember(record.id)}
        >
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const menuItems: MenuProps['items'] = [
    { key: 'knowledge', icon: <BookOutlined />, label: '知识库问答' },
    { key: 'quicklinks', icon: <LinkOutlined />, label: '快捷链接' },
    { key: 'members', icon: <UserOutlined />, label: '会员管理' },
    { key: 'system', icon: <GlobalOutlined />, label: '系统设置' },
    { type: 'divider' },
    { key: 'home', icon: <HomeOutlined />, label: '返回首页' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'home') {
      navigate('/');
    } else if (key === 'logout') {
      Modal.confirm({
        title: '确定退出登录吗？',
        onOk: () => {
          logout();
          message.success('已退出登录');
          navigate('/login');
        },
      });
    } else {
      setActiveMenu(key);
    }
  };

  return (
    <Layout className="admin-layout">
      <Sider width={200} theme="light" className="admin-sider">
        <div className="admin-logo-area">
          <span className="admin-title">管理后台</span>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[activeMenu]}
          onClick={handleMenuClick}
          items={menuItems}
          style={{ flex: 1, overflowY: 'auto', borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header className="admin-header-new">
          <div className="header-breadcrumb">
            {(menuItems as any[]).find(i => i?.key === activeMenu)?.label}
          </div>
        </Header>
        <Content className="admin-content-new">
          {activeMenu === 'knowledge' && (
            <Card
              title="AI 助手知识库"
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                  新增问答
                </Button>
              }
            >
              <Table
                columns={knowledgeColumns}
                dataSource={list}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          )}

          {activeMenu === 'quicklinks' && (
            <Card
              title="快捷链接管理"
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={openQuickLinkAdd}>
                  新增链接
                </Button>
              }
            >
              <Table
                columns={quickLinkColumns}
                dataSource={quickLinks}
                rowKey="id"
                loading={quickLinksLoading}
                pagination={false}
              />
            </Card>
          )}

          {activeMenu === 'members' && (
            <Card title="会员管理">
              <Table
                columns={memberColumns}
                dataSource={members}
                rowKey="id"
                loading={membersLoading}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          )}

          {activeMenu === 'system' && (
            <Card title="系统设置">
              <Tabs defaultActiveKey="basic">
                <Tabs.TabPane tab="基础设置" key="basic">
                  <Form
                    form={systemForm}
                    layout="vertical"
                    onFinish={onSystemSettingsFinish}
                    style={{ maxWidth: 600 }}
                  >
                    <Form.Item label="站点名称" name="site_name" rules={[{ required: true, message: '请输入站点名称' }]}>
                      <Input placeholder="例如：美家AI" />
                    </Form.Item>
                    <Form.Item label="Logo图" name="logo_url">
                      <Upload
                        listType="picture-card"
                        maxCount={1}
                        customRequest={customUpload}
                        fileList={systemSettings?.logo_url ? [{ uid: '-1', name: 'logo', status: 'done', url: getMediaUrl(systemSettings.logo_url) }] : []}
                        onRemove={() => {
                          setSystemSettings(prev => prev ? { ...prev, logo_url: '' } : null);
                          systemForm.setFieldsValue({ logo_url: '' });
                        }}
                        onChange={({ fileList }) => {
                          if (fileList.length > 0 && fileList[0].status === 'done') {
                            const url = fileList[0].response?.data?.url || fileList[0].url;
                            const relativeUrl = url ? (url.split('/static/uploads/').length > 1 ? `/static/uploads/${url.split('/static/uploads/')[1]}` : url) : '';
                            systemForm.setFieldsValue({ logo_url: relativeUrl });
                            setSystemSettings(prev => prev ? { ...prev, logo_url: relativeUrl } : null);
                          }
                        }}
                      >
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>上传</div>
                        </div>
                      </Upload>
                    </Form.Item>
                    <Form.Item label="AI图标" name="ai_icon_url">
                      <Upload
                        listType="picture-card"
                        maxCount={1}
                        customRequest={customUpload}
                        fileList={systemSettings?.ai_icon_url ? [{ uid: '-1', name: 'ai_icon', status: 'done', url: getMediaUrl(systemSettings.ai_icon_url) }] : []}
                        onRemove={() => {
                          setSystemSettings(prev => prev ? { ...prev, ai_icon_url: '' } : null);
                          systemForm.setFieldsValue({ ai_icon_url: '' });
                        }}
                        onChange={({ fileList }) => {
                          if (fileList.length > 0 && fileList[0].status === 'done') {
                            const url = fileList[0].response?.data?.url || fileList[0].url;
                            const relativeUrl = url ? (url.split('/static/uploads/').length > 1 ? `/static/uploads/${url.split('/static/uploads/')[1]}` : url) : '';
                            systemForm.setFieldsValue({ ai_icon_url: relativeUrl });
                            setSystemSettings(prev => prev ? { ...prev, ai_icon_url: relativeUrl } : null);
                          }
                        }}
                      >
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>上传</div>
                        </div>
                      </Upload>
                    </Form.Item>
                    <Form.Item label="电话号码" name="phone_number">
                      <Input placeholder="请输入联系电话" />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={systemSaveLoading}>
                        保存设置
                      </Button>
                    </Form.Item>
                  </Form>
                </Tabs.TabPane>
                <Tabs.TabPane tab="短信设置" key="sms">
                  <Form
                    form={smsForm}
                    layout="vertical"
                    onFinish={onSmsConfigFinish}
                    style={{ maxWidth: 600 }}
                  >
                    <Form.Item label="启用阿里云短信" name="use_aliyun" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item label="AccessKey ID" name="access_key_id">
                      <Input placeholder="请输入阿里云 AccessKey ID" />
                    </Form.Item>
                    <Form.Item label="AccessKey Secret" name="access_key_secret">
                      <Input.Password placeholder="请输入阿里云 AccessKey Secret (保存后不可见)" />
                    </Form.Item>
                    <Form.Item label="短信签名" name="sign_name">
                      <Input placeholder="请输入短信签名" />
                    </Form.Item>
                    <Form.Item label="模版代码" name="template_code">
                      <Input placeholder="请输入模版代码 (例如: SMS_123456)" />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={smsSaveLoading}>
                        保存短信配置
                      </Button>
                    </Form.Item>
                  </Form>
                </Tabs.TabPane>
              </Tabs>
            </Card>
          )}
        </Content>
      </Layout>

      {/* Knowledge QA Modal */}
      <Modal
        title={editingItem ? '编辑问答' : '新增问答'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={closeModal}
        confirmLoading={submitLoading}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="question" label="问题" rules={[{ required: true, message: '请输入问题' }]}>
            <Input.TextArea rows={2} placeholder="请输入用户可能会问的问题" />
          </Form.Item>
          <Form.Item name="answer" label="答案" rules={[{ required: true, message: '请输入答案' }]}>
            <Input.TextArea rows={6} placeholder="请输入 AI 助手的回答" />
          </Form.Item>
          
          <Form.Item label="相关图片">
            <Upload
              listType="picture-card"
              fileList={imageList}
              customRequest={customUpload}
              onChange={({ fileList }) => setImageList(fileList)}
              onPreview={(file) => window.open(file.url || file.thumbUrl)}
            >
              {imageList.length >= 5 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
              最多上传 5 张图片，支持 jpg、png、webp 等格式
            </div>
          </Form.Item>

          <Form.Item label="相关视频">
            <Dragger
              fileList={videoList}
              customRequest={customUpload}
              onChange={({ fileList }) => setVideoList(fileList)}
              maxCount={1}
              accept="video/*"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽视频文件到此区域上传</p>
              <p className="ant-upload-hint">仅支持上传 1 个视频文件，建议大小不超过 20MB</p>
            </Dragger>
          </Form.Item>

          <Form.Item name="sort_order" label="排序权重" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数值越大越靠前" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Quick Link Modal */}
      <Modal
        title={editingQuickLink ? '编辑快捷链接' : '新增快捷链接'}
        open={quickLinkModalVisible}
        onOk={onQuickLinkFinish}
        onCancel={() => setQuickLinkModalVisible(false)}
        confirmLoading={quickLinkSubmitLoading}
        width={600}
        destroyOnClose
      >
        <Form form={quickLinkForm} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="请输入快捷链接标题" />
          </Form.Item>
          <Form.Item name="description" label="简介">
            <Input.TextArea rows={2} placeholder="请输入简介" />
          </Form.Item>
          <Form.Item name="icon_svg" label="图标 (SVG代码)" tooltip="支持直接粘贴 SVG 代码，用于手机端展示">
            <Input.TextArea rows={4} placeholder="请输入 SVG 代码" />
          </Form.Item>
          <Form.Item label="图片 (PC端)" required>
            <Upload
              listType="picture-card"
              maxCount={1}
              customRequest={customUpload}
              fileList={quickLinkImageList}
              onChange={({ fileList }) => setQuickLinkImageList(fileList)}
            >
              {quickLinkImageList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item name="link_url" label="链接" rules={[{ required: true, message: '请输入链接' }]}>
            <Input placeholder="请输入跳转链接" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_visible" label="是否显示" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Admin;
